import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  XMarkIcon,
  PaperAirplaneIcon,
  EyeIcon,
  EyeSlashIcon,
  ChatBubbleLeftIcon,
  DocumentTextIcon,
  ClockIcon,
  UserIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  TagIcon,
  PhotoIcon,
  PlusIcon,
  XCircleIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '@/core/auth';
import { ticketingAPI } from '../api/ticketing.api';
import { MentionInput } from './MentionInput';
import type {
  Ticket,
  TicketComment,
  TicketCategory,
  TicketStatus,
  TicketPriority,
  TicketSource,
} from '../types/ticketing.types';
import './TicketDetailModal.css';
import './MentionInput.css';

interface LocalAttachment {
  id: string;
  name: string;
  type: string;
  data: string;
  size: number;
}

interface TicketDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: Ticket;
  onUpdate: () => void;
  onMentionRead?: () => void;
  categories: TicketCategory[];
  userRole?: string;
}

const STATUS_OPTIONS: { value: TicketStatus; label: string; color: string }[] = [
  { value: 'open', label: 'Ouvert', color: '#3b82f6' },
  { value: 'in_progress', label: 'En cours', color: '#f59e0b' },
  { value: 'pending', label: 'En attente', color: '#8b5cf6' },
  { value: 'to_be_tested', label: 'A tester', color: '#06b6d4' },
  { value: 'ready_for_dev', label: 'Prêt pour dev', color: '#22c55e' },
  { value: 'resolved', label: 'Résolu', color: '#10b981' },
  { value: 'closed', label: 'Fermé', color: '#6b7280' },
];

const PRIORITY_OPTIONS: { value: TicketPriority; label: string; color: string }[] = [
  { value: 'low', label: 'Basse', color: '#6b7280' },
  { value: 'medium', label: 'Moyenne', color: '#f59e0b' },
  { value: 'high', label: 'Haute', color: '#f97316' },
  { value: 'urgent', label: 'Urgente', color: '#ef4444' },
];

const SOURCE_CONFIG: Record<TicketSource, { label: string; color: string }> = {
  webapp: { label: 'WebApp', color: '#3b82f6' },
  pickit: { label: 'PickIt', color: '#10b981' },
  revelo: { label: 'Revelo', color: '#f97316' },
};

const STATUS_LABELS: Record<TicketStatus, string> = {
  open: 'Ouvert',
  in_progress: 'En cours',
  pending: 'En attente',
  to_be_tested: 'A tester',
  ready_for_dev: 'Prêt pour dev',
  resolved: 'Résolu',
  closed: 'Fermé',
  cancelled: 'Annulé',
};

// Laurian Detiffe's user ID for Claude button access
const CLAUDE_AUTHORIZED_USER_ID = '81285517-429c-49cc-a1c9-d7d7b5f8e476';

export const TicketDetailModal: React.FC<TicketDetailModalProps> = ({
  isOpen,
  onClose,
  ticket,
  onUpdate,
  onMentionRead,
  categories: _categories,
  userRole: _userRole = 'admin',
}) => {
  const { user } = useAuth();
  const isClaudeAuthorized = user?.id === CLAUDE_AUTHORIZED_USER_ID;
  const [comments, setComments] = useState<TicketComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(true);
  const [sendingComment, setSendingComment] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(ticket.status);
  const [currentPriority, setCurrentPriority] = useState(ticket.priority);
  const [previewImage, setPreviewImage] = useState<{ src: string; name: string } | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  // Attachment management state
  const [newAttachments, setNewAttachments] = useState<LocalAttachment[]>([]);
  const [attachmentsToRemove, setAttachmentsToRemove] = useState<string[]>([]);
  const [showAttachmentUpload, setShowAttachmentUpload] = useState(false);
  const [savingAttachments, setSavingAttachments] = useState(false);
  // Claude processing state
  const [claudeProcessing, setClaudeProcessing] = useState(false);
  const [implementationDoneProcessing, _setImplementationDoneProcessing] = useState(false);
  const [showConclusionModal, setShowConclusionModal] = useState(false);
  const [conclusionText, setConclusionText] = useState('');

  // Test feedback state
  const [showNotWorkingModal, setShowNotWorkingModal] = useState(false);
  const [notWorkingReason, setNotWorkingReason] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const commentsEndRef = useRef<HTMLDivElement>(null);
  const hasMarkedAsReadRef = useRef(false);
  const onMentionReadRef = useRef(onMentionRead);

  // Keep the ref updated
  useEffect(() => {
    onMentionReadRef.current = onMentionRead;
  }, [onMentionRead]);

  // Mark mentions as read when modal opens (only once per ticket)
  useEffect(() => {
    if (isOpen && !hasMarkedAsReadRef.current) {
      hasMarkedAsReadRef.current = true;
      const markAsRead = async () => {
        try {
          await ticketingAPI.markMentionsAsRead(ticket.id);
          onMentionReadRef.current?.();
        } catch (error) {
          console.error('Error marking mentions as read:', error);
        }
      };
      markAsRead();
    }
  }, [isOpen, ticket.id]);

  // Reset the ref when ticket changes
  useEffect(() => {
    hasMarkedAsReadRef.current = false;
  }, [ticket.id]);

  useEffect(() => {
    if (isOpen) {
      fetchComments();
      setCurrentStatus(ticket.status);
      setCurrentPriority(ticket.priority);
      // Reset attachment state when modal opens
      setNewAttachments([]);
      setAttachmentsToRemove([]);
      setShowAttachmentUpload(false);
    }
  }, [isOpen, ticket.id]);

  useEffect(() => {
    // Scroll to bottom when comments change
    if (commentsEndRef.current) {
      commentsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [comments]);

  const fetchComments = async () => {
    try {
      setLoadingComments(true);
      const response = await ticketingAPI.getComments(ticket.id);
      if (response.success) {
        setComments(response.data);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleSendComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      setSendingComment(true);
      await ticketingAPI.addComment(ticket.id, newComment);
      setNewComment('');
      fetchComments();
      onUpdate();
    } catch (error) {
      console.error('Error sending comment:', error);
    } finally {
      setSendingComment(false);
    }
  };

  const handleStatusChange = async (newStatus: TicketStatus) => {
    try {
      await ticketingAPI.updateTicket(ticket.id, { status: newStatus });
      setCurrentStatus(newStatus);
      onUpdate();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handlePriorityChange = async (newPriority: TicketPriority) => {
    try {
      await ticketingAPI.updateTicket(ticket.id, { priority: newPriority });
      setCurrentPriority(newPriority);
      onUpdate();
    } catch (error) {
      console.error('Error updating priority:', error);
    }
  };

  const handleToggleWatch = async () => {
    try {
      await ticketingAPI.toggleWatch(ticket.id);
      onUpdate();
    } catch (error) {
      console.error('Error toggling watch:', error);
    }
  };

  // Attachment handling functions
  const processFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target?.result as string;
      const newAttachment: LocalAttachment = {
        id: `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: file.name || `screenshot-${Date.now()}.png`,
        type: file.type,
        data: base64,
        size: file.size,
      };
      setNewAttachments(prev => [...prev, newAttachment]);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      Array.from(files).forEach(processFile);
    }
    e.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    if (files) {
      Array.from(files).forEach(processFile);
    }
  };

  const handleRemoveNewAttachment = (id: string) => {
    setNewAttachments(prev => prev.filter(a => a.id !== id));
  };

  const handleMarkExistingForRemoval = (id: string) => {
    setAttachmentsToRemove(prev => [...prev, id]);
  };

  const handleUndoRemoval = (id: string) => {
    setAttachmentsToRemove(prev => prev.filter(aid => aid !== id));
  };

  const handleSaveAttachments = async () => {
    if (newAttachments.length === 0 && attachmentsToRemove.length === 0) return;

    try {
      setSavingAttachments(true);
      await ticketingAPI.updateTicket(ticket.id, {
        attachments_to_add: newAttachments.map(a => ({
          name: a.name,
          type: a.type,
          data: a.data,
          size: a.size,
        })),
        attachments_to_remove: attachmentsToRemove,
      });
      setNewAttachments([]);
      setAttachmentsToRemove([]);
      setShowAttachmentUpload(false);
      onUpdate();
    } catch (error) {
      console.error('Error saving attachments:', error);
    } finally {
      setSavingAttachments(false);
    }
  };

  const hasAttachmentChanges = newAttachments.length > 0 || attachmentsToRemove.length > 0;

  // Claude processing handlers
  const handleTriggerClaudeAnalysis = async () => {
    try {
      setClaudeProcessing(true);
      const response = await ticketingAPI.triggerClaudeAnalysis(ticket.id);
      if (response.success) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error triggering Claude analysis:', error);
    } finally {
      setClaudeProcessing(false);
    }
  };

  const canTriggerClaude = !['resolved', 'closed'].includes(ticket.status) &&
    !['pending', 'analyzing'].includes(ticket.claude_status || '');

  // Mark Implementation Done handler - opens modal
  const handleOpenConclusionModal = () => {
    setConclusionText('');
    setShowConclusionModal(true);
  };

  const handleSubmitImplementationDone = () => {
    if (!conclusionText.trim()) return;

    const conclusionToSend = conclusionText.trim();

    // Close modal immediately
    setShowConclusionModal(false);
    setConclusionText('');

    // Process in background
    ticketingAPI.markImplementationDone(ticket.id, conclusionToSend)
      .then((response) => {
        if (response.success) {
          onUpdate();
        }
      })
      .catch((error) => {
        console.error('Error marking implementation done:', error);
      });
  };

  const canMarkImplementationDone = ticket.claude_status === 'prd_created' && ticket.prd_file_path;

  // Test feedback handlers
  const handleTestWorks = () => {
    ticketingAPI.submitTestFeedback(ticket.id, true)
      .then((response) => {
        if (response.success) {
          onUpdate();
        }
      })
      .catch((error) => {
        console.error('Error submitting feedback:', error);
      });
  };

  const handleTestNotWorks = () => {
    setNotWorkingReason('');
    setShowNotWorkingModal(true);
  };

  const handleSubmitNotWorking = () => {
    const reason = notWorkingReason.trim();
    setShowNotWorkingModal(false);
    setNotWorkingReason('');

    ticketingAPI.submitTestFeedback(ticket.id, false, reason || undefined)
      .then((response) => {
        if (response.success) {
          onUpdate();
        }
      })
      .catch((error) => {
        console.error('Error submitting feedback:', error);
      });
  };

  const showTestFeedback = ticket.status === 'to_be_tested';

  const getAttachmentUrl = (att: { data?: string; path?: string }): string => {
    if (att.data) return att.data;
    if (att.path) {
      const apiBaseUrl = import.meta.env.VITE_API_URL || 'https://myapp.chatelainxpert.be';
      return `${apiBaseUrl}/api/admin/bureau/ticketing/attachments/${att.path}`;
    }
    return '';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-BE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "à l'instant";
    if (diffMins < 60) return `il y a ${diffMins} min`;
    if (diffHours < 24) return `il y a ${diffHours}h`;
    if (diffDays < 7) return `il y a ${diffDays}j`;
    return formatDate(dateString);
  };

  const getInitials = (name: string) => {
    if (!name) return '??';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Format message with mention highlights
  const formatMessageWithMentions = (message: string) => {
    const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;

    while ((match = mentionRegex.exec(message)) !== null) {
      // Add text before mention
      if (match.index > lastIndex) {
        parts.push(message.slice(lastIndex, match.index));
      }
      // Add mention highlight
      const displayName = match[1];
      parts.push(
        <span key={match.index} className="mention-highlight">
          @{displayName}
        </span>
      );
      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < message.length) {
      parts.push(message.slice(lastIndex));
    }

    return parts.length > 0 ? parts : message;
  };

  // Handle close with animation
  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 250);
  };

  if (!isOpen) return null;

  return (
    <div className={`ticket-detail-overlay ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
      <div className={`ticket-detail-panel ${isClosing ? 'closing' : ''}`} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="ticket-modal-header">
          <div className="ticket-modal-header-top">
            <div className="ticket-modal-meta">
              <span className="ticket-modal-number">#{ticket.ticket_number}</span>
              <span className={`ticket-modal-status ${currentStatus}`}>
                {STATUS_LABELS[currentStatus]}
              </span>
              {ticket.source && (
                <span
                  className="ticket-modal-source"
                  style={{
                    backgroundColor: `${SOURCE_CONFIG[ticket.source].color}15`,
                    color: SOURCE_CONFIG[ticket.source].color,
                  }}
                >
                  {SOURCE_CONFIG[ticket.source].label}
                </span>
              )}
            </div>
            <div className="ticket-modal-header-actions">
              {/* Claude Analyze Button - Only for authorized user */}
              {isClaudeAuthorized && canTriggerClaude && (
                <button
                  className={`ticket-modal-action-btn ticket-claude-header-btn ${claudeProcessing ? 'processing' : ''}`}
                  onClick={handleTriggerClaudeAnalysis}
                  disabled={claudeProcessing}
                  title="Analyser avec Claude"
                >
                  {claudeProcessing ? (
                    <div className="ticket-loading-spinner-small" />
                  ) : (
                    <CpuChipIcon className="w-5 h-5" />
                  )}
                </button>
              )}
              {/* Mark Implementation Done Button - Only for authorized user when PRD exists */}
              {isClaudeAuthorized && canMarkImplementationDone && (
                <button
                  className="ticket-modal-action-btn ticket-impl-done-btn"
                  onClick={handleOpenConclusionModal}
                  title="Marquer implémentation terminée"
                >
                  <CheckCircleIcon className="w-5 h-5" />
                </button>
              )}
              <button
                className={`ticket-modal-action-btn ${ticket.is_watching ? 'watching' : ''}`}
                onClick={handleToggleWatch}
                title={ticket.is_watching ? 'Ne plus suivre' : 'Suivre'}
              >
                {ticket.is_watching ? (
                  <EyeSlashIcon className="w-5 h-5" />
                ) : (
                  <EyeIcon className="w-5 h-5" />
                )}
              </button>
              <button className="ticket-modal-action-btn ticket-modal-close" onClick={handleClose}>
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
          <h2 className="ticket-modal-title">{ticket.title}</h2>
        </div>

        {/* Body */}
        <div className="ticket-modal-body">
          {/* Quick Actions Bar */}
          <div className="ticket-quick-bar">
            {/* Status Dropdown */}
            <div className="ticket-dropdown-container">
              <button
                className="ticket-badge-btn"
                style={{ '--badge-color': STATUS_OPTIONS.find(s => s.value === currentStatus)?.color } as React.CSSProperties}
                onClick={() => {
                  setShowStatusDropdown(!showStatusDropdown);
                  setShowPriorityDropdown(false);
                }}
              >
                <CheckCircleIcon />
                {STATUS_OPTIONS.find(s => s.value === currentStatus)?.label}
              </button>
              {showStatusDropdown && (
                <div className="ticket-dropdown">
                  {STATUS_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      className={`ticket-dropdown-item ${currentStatus === opt.value ? 'active' : ''}`}
                      style={{ '--item-color': opt.color } as React.CSSProperties}
                      onClick={() => {
                        handleStatusChange(opt.value);
                        setShowStatusDropdown(false);
                      }}
                    >
                      <span className="ticket-dropdown-dot" />
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Priority Dropdown */}
            <div className="ticket-dropdown-container">
              <button
                className="ticket-badge-btn"
                style={{ '--badge-color': PRIORITY_OPTIONS.find(p => p.value === currentPriority)?.color } as React.CSSProperties}
                onClick={() => {
                  setShowPriorityDropdown(!showPriorityDropdown);
                  setShowStatusDropdown(false);
                }}
              >
                <ExclamationTriangleIcon />
                {PRIORITY_OPTIONS.find(p => p.value === currentPriority)?.label}
              </button>
              {showPriorityDropdown && (
                <div className="ticket-dropdown">
                  {PRIORITY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      className={`ticket-dropdown-item ${currentPriority === opt.value ? 'active' : ''}`}
                      style={{ '--item-color': opt.color } as React.CSSProperties}
                      onClick={() => {
                        handlePriorityChange(opt.value);
                        setShowPriorityDropdown(false);
                      }}
                    >
                      <span className="ticket-dropdown-dot" />
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Category Badge (display only) */}
            {ticket.category_name && (
              <div
                className="ticket-badge-btn ticket-badge-static"
                style={{ '--badge-color': ticket.category_color || '#6b7280' } as React.CSSProperties}
              >
                <TagIcon />
                {ticket.category_name}
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="ticket-metadata">
            <div className="ticket-meta-item">
              <UserIcon />
              <div className="ticket-meta-content">
                <span className="ticket-meta-label">Créé par</span>
                <span className="ticket-meta-value">{ticket.created_by_name}</span>
              </div>
            </div>
            <div className="ticket-meta-item">
              <ClockIcon />
              <div className="ticket-meta-content">
                <span className="ticket-meta-label">Date</span>
                <span className="ticket-meta-value">{formatRelativeTime(ticket.created_at)}</span>
              </div>
            </div>
          </div>

          {/* Description */}
          {ticket.description && (
            <div className="ticket-modal-section">
              <div className="ticket-modal-section-header">
                <DocumentTextIcon />
                Description
              </div>
              <div className="ticket-modal-description">
                {ticket.description}
              </div>
            </div>
          )}

          {/* Attachments Section */}
          <div className="ticket-modal-section">
            <div className="ticket-modal-section-header ticket-attachments-header">
              <PhotoIcon />
              Pièces jointes
              <span className="ticket-attachments-count">
                {(ticket.metadata?.attachments?.length || 0) - attachmentsToRemove.length + newAttachments.length}
              </span>
              <button
                type="button"
                className="ticket-add-attachment-btn"
                onClick={() => setShowAttachmentUpload(!showAttachmentUpload)}
              >
                <PlusIcon className="w-4 h-4" />
                Ajouter
              </button>
            </div>

            {/* Existing Attachments */}
            {ticket.metadata?.attachments && ticket.metadata.attachments.length > 0 && (
              <div className="ticket-attachments">
                {ticket.metadata.attachments.map((att: any) => {
                  const isMarkedForRemoval = attachmentsToRemove.includes(att.id);
                  return (
                    <div
                      key={att.id}
                      className={`ticket-attachment ${isMarkedForRemoval ? 'marked-for-removal' : ''}`}
                    >
                      <div
                        className="ticket-attachment-preview"
                        onClick={() => !isMarkedForRemoval && setPreviewImage({ src: getAttachmentUrl(att), name: att.name })}
                      >
                        <img src={getAttachmentUrl(att)} alt={att.name} />
                        {isMarkedForRemoval && (
                          <div className="ticket-attachment-removed-overlay">Supprimé</div>
                        )}
                      </div>
                      <div className="ticket-attachment-info">
                        <span className="ticket-attachment-name">{att.name}</span>
                        {isMarkedForRemoval ? (
                          <button
                            type="button"
                            className="ticket-attachment-undo"
                            onClick={() => handleUndoRemoval(att.id)}
                          >
                            Annuler
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="ticket-attachment-delete"
                            onClick={() => handleMarkExistingForRemoval(att.id)}
                          >
                            <XCircleIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* New Attachments Preview */}
            {newAttachments.length > 0 && (
              <div className="ticket-new-attachments">
                <div className="ticket-new-attachments-label">Nouvelles pièces jointes:</div>
                <div className="ticket-attachments">
                  {newAttachments.map((att) => (
                    <div key={att.id} className="ticket-attachment ticket-attachment-new">
                      <div className="ticket-attachment-preview">
                        <img src={att.data} alt={att.name} />
                      </div>
                      <div className="ticket-attachment-info">
                        <span className="ticket-attachment-name">{att.name}</span>
                        <button
                          type="button"
                          className="ticket-attachment-delete"
                          onClick={() => handleRemoveNewAttachment(att.id)}
                        >
                          <XCircleIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upload Zone */}
            {showAttachmentUpload && (
              <div
                className="ticket-attachment-dropzone"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <PhotoIcon className="w-8 h-8" />
                <p>Cliquez ou glissez-déposez une image</p>
                <span>PNG, JPG, GIF jusqu'à 5MB</span>
              </div>
            )}

            {/* Save Attachments Button */}
            {hasAttachmentChanges && (
              <div className="ticket-attachment-actions">
                <button
                  type="button"
                  className="ticket-modal-btn ticket-modal-btn-secondary"
                  onClick={() => {
                    setNewAttachments([]);
                    setAttachmentsToRemove([]);
                  }}
                  disabled={savingAttachments}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="ticket-modal-btn ticket-modal-btn-primary"
                  onClick={handleSaveAttachments}
                  disabled={savingAttachments}
                >
                  {savingAttachments ? 'Enregistrement...' : 'Enregistrer les modifications'}
                </button>
              </div>
            )}
          </div>

          {/* Comments */}
          <div className="ticket-comments-section">
            <div className="ticket-comments-header">
              <ChatBubbleLeftIcon />
              Commentaires
              <span className="ticket-comments-count">{comments.length}</span>
            </div>

            <div className="ticket-comments-list">
              {loadingComments ? (
                <div className="ticket-comments-loading">
                  <div className="ticket-loading-spinner" />
                  <span>Chargement...</span>
                </div>
              ) : comments.length === 0 ? (
                <div className="ticket-comments-empty">
                  <ChatBubbleLeftIcon />
                  <span>Aucun commentaire</span>
                </div>
              ) : (
                <>
                  {comments.map((comment) => (
                    <div key={comment.id} className="ticket-comment">
                      <div className="ticket-comment-avatar">
                        {getInitials(comment.user_name || '')}
                      </div>
                      <div className="ticket-comment-content">
                        <div className="ticket-comment-bubble">
                          <div className="ticket-comment-header">
                            <span className="ticket-comment-author">{comment.user_name}</span>
                            <span className="ticket-comment-time">
                              {formatRelativeTime(comment.created_at)}
                            </span>
                          </div>
                          <p className="ticket-comment-text">{formatMessageWithMentions(comment.message)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={commentsEndRef} />
                </>
              )}

              {/* Test Feedback - at bottom of comments */}
              {showTestFeedback && (
                <div className="ticket-test-feedback-section">
                  <span className="ticket-test-feedback-text">Est-ce que ça fonctionne ?</span>
                  <div className="ticket-test-feedback-buttons">
                    <button
                      className="ticket-test-btn ticket-test-btn-success"
                      onClick={handleTestWorks}
                    >
                      <CheckCircleIcon className="w-4 h-4" />
                      Ça marche !
                    </button>
                    <button
                      className="ticket-test-btn ticket-test-btn-danger"
                      onClick={handleTestNotWorks}
                    >
                      <XCircleIcon className="w-4 h-4" />
                      Ça ne marche pas
                    </button>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>

        {/* Comment Input - Fixed at bottom */}
        <div className="ticket-comment-input-wrapper">
          <form onSubmit={handleSendComment} className="ticket-comment-input-form">
            <MentionInput
              value={newComment}
              onChange={setNewComment}
              placeholder="Ecrire un commentaire... (@pour mentionner)"
              rows={1}
              disabled={sendingComment}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendComment(e);
                }
              }}
            />
            <button
              type="submit"
              className="ticket-comment-send"
              disabled={sendingComment || !newComment.trim()}
            >
              <PaperAirplaneIcon />
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="ticket-modal-footer">
          <button className="ticket-modal-btn ticket-modal-btn-secondary" onClick={handleClose}>
            Fermer
          </button>
        </div>

        {/* Image Preview Modal */}
        {previewImage && (
          <div className="ticket-image-preview-overlay" onClick={() => setPreviewImage(null)}>
            <div className="ticket-image-preview-container" onClick={(e) => e.stopPropagation()}>
              <div className="ticket-image-preview-header">
                <span className="ticket-image-preview-name">{previewImage.name}</span>
                <button
                  className="ticket-image-preview-close"
                  onClick={() => setPreviewImage(null)}
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              <div className="ticket-image-preview-content">
                <img src={previewImage.src} alt={previewImage.name} />
              </div>
              <div className="ticket-image-preview-footer">
                <a
                  href={previewImage.src}
                  download={previewImage.name}
                  className="ticket-modal-btn ticket-modal-btn-secondary"
                  onClick={(e) => e.stopPropagation()}
                >
                  Télécharger
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Implementation Done Conclusion Modal */}
        {showConclusionModal && (
          <div className="ticket-image-preview-overlay" onClick={() => setShowConclusionModal(false)}>
            <div className="ticket-conclusion-modal" onClick={(e) => e.stopPropagation()}>
              <div className="ticket-conclusion-header">
                <h3>C'est prêt !</h3>
                <button
                  className="ticket-image-preview-close"
                  onClick={() => setShowConclusionModal(false)}
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              <div className="ticket-conclusion-body">
                <p className="ticket-conclusion-description">
                  Décris ce qui a été fait (tu peux écrire en technique). L'IA transformera ton texte en résumé simple pour l'utilisateur.
                </p>
                <textarea
                  className="ticket-conclusion-textarea"
                  placeholder="Ex: Added delete button with confirmation. Only visible for message author. Soft delete implemented..."
                  value={conclusionText}
                  onChange={(e) => setConclusionText(e.target.value)}
                  rows={5}
                  autoFocus
                />
              </div>
              <div className="ticket-conclusion-footer">
                <button
                  className="ticket-modal-btn ticket-modal-btn-secondary"
                  onClick={() => setShowConclusionModal(false)}
                  disabled={implementationDoneProcessing}
                >
                  Annuler
                </button>
                <button
                  className="ticket-modal-btn ticket-modal-btn-primary"
                  onClick={handleSubmitImplementationDone}
                  disabled={implementationDoneProcessing || !conclusionText.trim()}
                >
                  {implementationDoneProcessing ? 'Envoi...' : 'Valider et notifier'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Not Working Feedback Modal */}
        {showNotWorkingModal && (
          <div className="ticket-image-preview-overlay" onClick={() => setShowNotWorkingModal(false)}>
            <div className="ticket-conclusion-modal ticket-not-working-modal" onClick={(e) => e.stopPropagation()}>
              <div className="ticket-conclusion-header ticket-not-working-header">
                <h3>Qu'est-ce qui ne fonctionne pas ?</h3>
                <button
                  className="ticket-image-preview-close"
                  onClick={() => setShowNotWorkingModal(false)}
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              <div className="ticket-conclusion-body">
                <p className="ticket-conclusion-description">
                  Décris le problème rencontré pour aider le développeur à corriger.
                </p>
                <textarea
                  className="ticket-conclusion-textarea"
                  placeholder="Ex: Le bouton n'apparaît pas, j'ai une erreur quand je clique..."
                  value={notWorkingReason}
                  onChange={(e) => setNotWorkingReason(e.target.value)}
                  rows={4}
                  autoFocus
                />
              </div>
              <div className="ticket-conclusion-footer">
                <button
                  className="ticket-modal-btn ticket-modal-btn-secondary"
                  onClick={() => setShowNotWorkingModal(false)}
                >
                  Annuler
                </button>
                <button
                  className="ticket-modal-btn ticket-modal-btn-danger"
                  onClick={handleSubmitNotWorking}
                >
                  Envoyer le retour
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};
